import { motion, useAnimation } from "motion/react";
import { animationCurves } from "@utils/animationCurves";
import { useIsDesktop } from "@hooks/useIsDesktop";
import { useResizeObserver } from "@react-hookz/web";

type TProps = {
  value: number
  isPercentage?: boolean
}

const NumberAnimation = ({
  value,
  isPercentage = false
}: TProps) => {
  const isDesktop = useIsDesktop();
  const stringValue = value.toLocaleString();
  const controls = useAnimation();
  const chars = [];

  const lineHeight = isDesktop ? 240 : 80;

  const animationVariants = {
    initial: (custom: Record<string, string | number>) => {
      const { digit, direction } = custom;
      return {
        y: -lineHeight * (10 + Number(digit) * Number(direction))
      }
    },
    visible: (custom: Record<string, string | number>) => {
      const { digit, direction } = custom;
      return {
        y: -lineHeight * (10 - Number(digit) * Number(direction))
      }
    }
  }

  useResizeObserver(document.documentElement, () => {
    // When resizing screen, set the animation to
    // the initial value, ensuring that the animation
    // keeps consistent.
    controls.set("initial");
  })

  for (const c of stringValue) {
    chars.push(c)
  }

  return (
    <div>
      <motion.h2 className="text-[88px] dsk:text-[278px] text-heading-1 leading-[82px] dsk:leading-[240px] flex">
        {
          chars.map((c, idx) => {
            const direction = idx % 2 === 0 ? 1 : -1;
            if (isNaN(Number(c))) {
              return (
                <span key={idx} className="h-20 dsk:h-64">{c}</span>
              )
            }
            return (
              <motion.span
                key={idx}
                className="h-20 dsk:h-64 justify-start overflow-hidden"
              >
                <motion.div
                  variants={animationVariants}
                  initial="initial"
                  whileInView="visible"
                  animate={controls}
                  custom={{ digit: c, direction }}
                  transition={animationCurves.long}
                >
                  <Digit value={c} />
                </motion.div>
              </motion.span>
            )
          })
        }
        {isPercentage && '%'}
      </motion.h2>
    </div>
  )
}

const digits = ["0", "9", "8", "7", "6", "5", "4", "3", "2", "1", "0", "1", "2", "3", "4", "5", "6", "7", "8", "9"];

const Digit = ({ value } : {value: string}) => {
  if (isNaN(Number(value))) {
    return <>{value}</>;
  }
  return (
    <>
    {
      digits.map((d, idx) => (
        <div
          key={`${d}-${idx}`}
          className="shrink-0 text-center h-20 dsk:h-60"
          >{d}</div>
      ))
    }
    </>
  )
}

export default NumberAnimation;